import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  Link,
  LinkOff,
  Sync,
  Visibility,
  Edit,
  CheckCircle,
  Error,
  Warning,
  AccountBalance,
  AccountCircle,
  Refresh,
} from '@mui/icons-material';
import { AccountLinking } from '../types';

const AccountLinkingComponent: React.FC = () => {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<AccountLinking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountLinking | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [newLinkData, setNewLinkData] = useState({
    bankAccountId: '',
    waveAccountId: '',
    merchantId: '',
  });

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoading(true);
        // Mock data - replace with actual API call
        const mockAccounts: AccountLinking[] = [
          {
            id: '1',
            bankAccountId: 'BANK001',
            waveAccountId: 'WAVE001',
            merchantId: 'MERCH001',
            status: 'active',
            linkedDate: '2024-01-15T10:00:00Z',
            lastSyncDate: '2024-02-15T14:30:00Z',
            syncStatus: 'success',
          },
          {
            id: '2',
            bankAccountId: 'BANK002',
            waveAccountId: 'WAVE002',
            merchantId: 'MERCH002',
            status: 'active',
            linkedDate: '2024-01-20T14:30:00Z',
            lastSyncDate: '2024-02-16T09:15:00Z',
            syncStatus: 'success',
          },
          {
            id: '3',
            bankAccountId: 'BANK003',
            waveAccountId: 'WAVE003',
            merchantId: 'MERCH003',
            status: 'pending',
            linkedDate: '2024-02-01T09:15:00Z',
            lastSyncDate: '2024-02-01T09:15:00Z',
            syncStatus: 'pending',
          },
          {
            id: '4',
            bankAccountId: 'BANK004',
            waveAccountId: 'WAVE004',
            merchantId: 'MERCH004',
            status: 'inactive',
            linkedDate: '2024-01-10T16:45:00Z',
            lastSyncDate: '2024-02-10T16:45:00Z',
            syncStatus: 'failed',
          },
        ];

        setAccounts(mockAccounts);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch account data');
        setLoading(false);
      }
    };

    fetchAccounts();
  }, []);

  const filteredAccounts = accounts.filter(account => {
    if (filterStatus !== 'all' && account.status !== filterStatus) return false;
    return true;
  });

  const activeAccounts = filteredAccounts.filter(account => account.status === 'active');
  const pendingAccounts = filteredAccounts.filter(account => account.status === 'pending');
  const inactiveAccounts = filteredAccounts.filter(account => account.status === 'inactive');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'inactive': return 'error';
      default: return 'default';
    }
  };

  const getSyncStatusColor = (syncStatus: string) => {
    switch (syncStatus) {
      case 'success': return 'success';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getSyncStatusIcon = (syncStatus: string) => {
    switch (syncStatus) {
      case 'success': return <CheckCircle color="success" />;
      case 'failed': return <Error color="error" />;
      case 'pending': return <Warning color="warning" />;
      default: return <Warning color="warning" />;
    }
  };

  const handleNewLink = () => {
    setNewLinkData({ bankAccountId: '', waveAccountId: '', merchantId: '' });
    setActiveStep(0);
    setDialogOpen(true);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCreateLink = () => {
    // Handle creating new account link - replace with actual API call
    const newAccount: AccountLinking = {
      id: Date.now().toString(),
      ...newLinkData,
      status: 'pending',
      linkedDate: new Date().toISOString(),
      lastSyncDate: new Date().toISOString(),
      syncStatus: 'pending',
    };

    setAccounts([...accounts, newAccount]);
    setDialogOpen(false);
    setActiveStep(0);
  };

  const handleSyncAccount = (accountId: string) => {
    // Handle syncing account - replace with actual API call
    setAccounts(accounts.map(account => 
      account.id === accountId 
        ? { ...account, syncStatus: 'pending', lastSyncDate: new Date().toISOString() }
        : account
    ));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Bank-to-Wave Account Linking
      </Typography>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Linked Accounts
                </Typography>
                <Typography variant="h5" component="div">
                  {filteredAccounts.length}
                </Typography>
              </Box>
              <Link color="primary" />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Active Links
                </Typography>
                <Typography variant="h5" component="div" color="success.main">
                  {activeAccounts.length}
                </Typography>
              </Box>
              <CheckCircle color="success" />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Pending Links
                </Typography>
                <Typography variant="h5" component="div" color="warning.main">
                  {pendingAccounts.length}
                </Typography>
              </Box>
              <Warning color="warning" />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Inactive Links
                </Typography>
                <Typography variant="h5" component="div" color="error.main">
                  {inactiveAccounts.length}
                </Typography>
              </Box>
              <LinkOff color="error" />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<Link />}
          onClick={handleNewLink}
        >
          Link New Account
        </Button>
        <Button
          variant="outlined"
          startIcon={<Sync />}
          onClick={() => {/* Handle bulk sync */}}
        >
          Sync All Accounts
        </Button>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {/* Handle refresh */}}
        >
          Refresh Status
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={filterStatus}
              label={t('accountLinking.statusFilter')}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Accounts Table */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Linked Accounts ({filteredAccounts.length})
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Bank Account</TableCell>
                <TableCell>Wave Account</TableCell>
                <TableCell>Merchant ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Linked Date</TableCell>
                <TableCell>Last Sync</TableCell>
                <TableCell>Sync Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAccounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AccountBalance color="primary" />
                      {account.bankAccountId}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AccountCircle color="secondary" />
                      {account.waveAccountId}
                    </Box>
                  </TableCell>
                  <TableCell>{account.merchantId}</TableCell>
                  <TableCell>
                    <Chip
                      label={account.status}
                      color={getStatusColor(account.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(account.linkedDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {new Date(account.lastSyncDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getSyncStatusIcon(account.syncStatus)}
                      <Chip
                        label={account.syncStatus}
                        color={getSyncStatusColor(account.syncStatus) as any}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title={t('accountLinking.viewDetails')}>
                        <IconButton size="small">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('accountLinking.editLink')}>
                        <IconButton size="small" color="primary">
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('accountLinking.syncAccount')}>
                        <IconButton 
                          size="small" 
                          color="success"
                          onClick={() => handleSyncAccount(account.id)}
                        >
                          <Sync />
                        </IconButton>
                      </Tooltip>
                                              <Tooltip title="Unlink Account">
                          <IconButton size="small" color="error">
                            <LinkOff />
                          </IconButton>
                        </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* New Link Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Link New Bank-to-Wave Account</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} orientation="vertical">
            <Step>
              <StepLabel>Bank Account Details</StepLabel>
              <StepContent>
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Bank Account ID"
                    value={newLinkData.bankAccountId}
                    onChange={(e) => setNewLinkData({ ...newLinkData, bankAccountId: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!newLinkData.bankAccountId}
                  >
                    Continue
                  </Button>
                </Box>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Wave Account Details</StepLabel>
              <StepContent>
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Wave Account ID"
                    value={newLinkData.waveAccountId}
                    onChange={(e) => setNewLinkData({ ...newLinkData, waveAccountId: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!newLinkData.waveAccountId}
                    sx={{ mr: 1 }}
                  >
                    Continue
                  </Button>
                  <Button onClick={handleBack}>Back</Button>
                </Box>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Merchant Association</StepLabel>
              <StepContent>
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Merchant ID"
                    value={newLinkData.merchantId}
                    onChange={(e) => setNewLinkData({ ...newLinkData, merchantId: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleCreateLink}
                    disabled={!newLinkData.merchantId}
                    sx={{ mr: 1 }}
                  >
                    Create Link
                  </Button>
                  <Button onClick={handleBack}>Back</Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountLinkingComponent;
